import { Controller } from "@hotwired/stimulus"
import { mountCardElements } from "../stripe/card_elements"
import { setHidden } from "../dom/visibility"

const GENERIC_STRIPE_ERROR =
  "There has been an error contacting our payment provider. Please refresh " +
  "this page and try again. If this issue persists, please contact " +
  "support@echoleft.com"

export default class extends Controller {
  static targets = [
    "picker",
    "creator",
    "viewer",
    "cardNumber",
    "cardExpiry",
    "cardCvc",
    "submitButton",
    "storedCheckbox",
    "errorMessage",
    "cardIcon",
    "cardLast4"
  ]

  static values = {
    paymentApiReference: String,
    paymentIntentId: String,
    stripePublishableKey: String,
    renderAddress: Boolean,
    storedPaymentMethods: Object,
    selectedPaymentMethod: Object,
    billingDetails: Object
  }

  connect() {
    this.stripe = window.Stripe(this.stripePublishableKeyValue)
    this.storedByUser = false
    this.billingDetails = this.renderAddressValue ? { ...this.billingDetailsValue } : null
    this.selectedPaymentMethod = this.isPresent(this.selectedPaymentMethodValue)
      ? this.selectedPaymentMethodValue
      : null
    this.render()
  }

  isPresent(value) {
    return Boolean(value) && Object.keys(value).length > 0
  }

  render() {
    this.selectedPaymentMethod ? this.showViewer() : this.showPickerAndCreator()
  }

  showPickerAndCreator() {
    setHidden(this.viewerTarget, true)
    setHidden(this.creatorTarget, false)
    this.renderPicker()
    this.mountCardForm()
  }

  renderPicker() {
    const methods = (this.storedPaymentMethodsValue && this.storedPaymentMethodsValue.data) || []

    if (methods.length === 0) {
      setHidden(this.pickerTarget, true)
      this.pickerTarget.innerHTML = ""
      return
    }

    setHidden(this.pickerTarget, false)
    this.pickerTarget.innerHTML = methods
      .map(
        (method) => `
          <div data-payment-method-id="${method.id}" data-action="click->payment-method#selectStored">
            I am a payment method! ${method.id}
          </div>
        `
      )
      .join("")
  }

  selectStored(event) {
    const id = event.currentTarget.dataset.paymentMethodId
    const methods = (this.storedPaymentMethodsValue && this.storedPaymentMethodsValue.data) || []
    const paymentMethod = methods.find((method) => String(method.id) === String(id))
    if (!paymentMethod) return

    this.attachPaymentMethod(
      { data: paymentMethod },
      "PATCH",
      `/api/v1/payments/${this.paymentApiReferenceValue}/payment_methods/`
    )
  }

  showViewer() {
    setHidden(this.creatorTarget, true)
    setHidden(this.viewerTarget, false)

    const cardDetails = this.cardDetailsFor(this.selectedPaymentMethod)
    if (!cardDetails) return

    const brand = (cardDetails.card.brand || "").replace(/ /g, "-")
    this.cardIconTarget.className = `payment-card-summary__card-icon payment-card-icon--${brand}`
    this.cardLast4Target.textContent = cardDetails.card.last4 || ""
  }

  cardDetailsFor(paymentMethod) {
    if (!paymentMethod) return null

    const attrs = paymentMethod.attributes || paymentMethod
    let response = attrs.provider_response
    if (typeof response === "string") {
      try {
        response = JSON.parse(response)
      } catch (error) {
        return null
      }
    }

    return response || null
  }

  changeMethod() {
    fetch(`/api/v1/payments/${this.paymentApiReferenceValue}/payment_methods`, {
      method: "DELETE",
      headers: this.jsonHeaders()
    }).then(() => {
      this.selectedPaymentMethod = null
      this.render()
    })
  }

  mountCardForm() {
    if (this.cardElements) return
    this.cardElements = mountCardElements(this.stripe, {
      cardNumber: this.cardNumberTarget,
      cardExpiry: this.cardExpiryTarget,
      cardCvc: this.cardCvcTarget
    })
  }

  toggleStored() {
    this.storedByUser = this.storedCheckboxTarget.checked
  }

  updateBillingField(event) {
    const parts = event.target.dataset.fieldname.split(".")
    let target = this.billingDetails
    for (let i = 0; i < parts.length - 1; i += 1) {
      target = target[parts[i]]
    }
    target[parts[parts.length - 1]] = event.target.value
  }

  async submitCard(event) {
    event.preventDefault()
    this.clearError()

    const validationError = this.billingValidationError()
    if (validationError) {
      this.showError(validationError)
      return
    }

    this.submitButtonTarget.textContent = "Loading, please wait."

    let response
    try {
      response = await this.stripe.createPaymentMethod({
        type: "card",
        card: this.cardElements.cardNumberElement,
        billing_details: this.billingDetails
      })
    } catch (error) {
      this.showError(GENERIC_STRIPE_ERROR)
      return
    }

    if (response.error) {
      this.showError(response.error.message)
      return
    }

    await this.createPaymentMethod(response.paymentMethod)
  }

  billingValidationError() {
    if (!this.renderAddressValue) return null
    if (!this.billingDetails || !this.billingDetails.name) {
      return "Please enter Cardholder Name."
    }
    return null
  }

  showError(message) {
    this.submitButtonTarget.textContent = "Confirm Card Details"
    setHidden(this.errorMessageTarget, false)
    this.errorMessageTarget.textContent = message
  }

  clearError() {
    setHidden(this.errorMessageTarget, true)
    this.errorMessageTarget.textContent = ""
  }

  async createPaymentMethod(stripeResponse) {
    const body = {
      data: {
        type: "payment_methods",
        attributes: {
          provider_id: stripeResponse.id,
          method_type: stripeResponse.type,
          provider_response: stripeResponse,
          stored_by_user: this.storedByUser
        }
      },
      meta: {
        payment_api_reference: this.paymentApiReferenceValue
      }
    }

    if (this.paymentIntentIdValue) {
      body.meta.payment_intent_id = this.paymentIntentIdValue
    }

    await this.attachPaymentMethod(
      body,
      "POST",
      `/api/v1/payments/${this.paymentApiReferenceValue}/payment_methods`
    )
  }

  async attachPaymentMethod(body, method, url) {
    try {
      const response = await fetch(url, {
        method,
        headers: this.jsonHeaders(),
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error((data.error && data.error.message) || "There was an issue with your payment.")
      }

      this.selectedPaymentMethod = data.data
      this.submitButtonTarget.textContent = "Confirm Card Details"
      this.render()
    } catch (error) {
      this.showError(error.message)
    }
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/vnd.api+json",
      "X-CSRF-Token": this.csrfToken,
      Accept: "application/json"
    }
  }

  get csrfToken() {
    const csrfMetaTag = document.querySelector("meta[name='csrf-token']")
    return csrfMetaTag ? csrfMetaTag.content : ""
  }
}
